import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchLocationMapView } from '../common/fetch';
import { NOT_FOUND } from './status';

/**
 * @typedef {import ('../propTypes/map/location').Location} Location
 * @typedef {import ('../propTypes/map/location').BoundingBox} BoundingBox
 */

export const SET_MAPV2_LOCATION = 'SET_MAPV2_LOCATION';
export const SET_MAPV2_LOCATION_LOADED = 'SET_MAPV2_LOCATION_LOADED';
export const SET_MAPV2_BOUNDING_BOX = 'SET_MAPV2_BOUNDING_BOX';
export const SET_MAPV2_ERROR = 'SET_MAPV2_ERROR';
export const SET_MAPV2_LOADING = 'SET_MAPV2_LOADING';
export const SET_MAPV2_MESSAGE = 'SET_MAPV2_MESSAGE';
export const FETCH_LOCATION_VIEW_V2 = 'FETCH_LOCATION_VIEW_V2';
export const RESET_MAP_STATE = 'RESET_MAP_STATE';
export const ZOOM_IN = 'ZOOM_IN';

/**
 * @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<Location, SET_MAPV2_LOCATION>}
 */
export const setMapLocation = createAction(SET_MAPV2_LOCATION);

/**
 * @type {import('@reduxjs/toolkit').ActionCreatorWithoutPayload<SET_MAPV2_LOCATION_LOADED>}
 */
export const setMapLocationLoaded = createAction(SET_MAPV2_LOCATION_LOADED);

/**
 * @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<BoundingBox, SET_MAPV2_BOUNDING_BOX>}
 */
export const setMapBoundingBox = createAction(SET_MAPV2_BOUNDING_BOX);

/**
 * @type {import('@reduxjs/toolkit').ActionCreatorWithOptionalPayload<{ message: string, status: number }, SET_MAPV2_ERROR>}
 */
export const setMapError = createAction(SET_MAPV2_ERROR);

/**
 * @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<boolean, SET_MAPV2_LOADING>}
 */
export const setMapLoading = createAction(SET_MAPV2_LOADING);

/**
 * @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<string, SET_MAPV2_MESSAGE>}
 */
export const setMapMessage = createAction(SET_MAPV2_MESSAGE);

/**
 * @type {import('@reduxjs/toolkit').ActionCreatorWithoutPayload<RESET_MAP_STATE>}
 */
export const resetMapState = createAction(RESET_MAP_STATE);

/**
 * @type {import('@reduxjs/toolkit').ActionCreatorWithOptionalPayload<{ center: Location['center'] }, ZOOM_IN>}
 */
export const zoomIn = createAction(ZOOM_IN);

export const fetchLocationView = createAsyncThunk(
  FETCH_LOCATION_VIEW_V2,
  /**
   * @param {string | number} geonameId
   */
  async (geonameId, thunkAPI) => {
    try {
      if (parseInt(geonameId, 10)) {
        const response = await fetchLocationMapView(geonameId);
        const { taxonomy, travelContent, boundingBox, breadCrumbs, siblings, url } = response;
        return {
          boundingBox,
          taxonomy,
          travelContent,
          breadCrumbs,
          siblings,
          url,
        };
      }
      throw new Error('geonameId is not a number');
    } catch (error) {
      if (error.status === 400) {
        const message = 'The location could not be found';
        thunkAPI.dispatch({
          type: NOT_FOUND,
          message,
        });
        throw new Error(message);
      }
      throw error;
    }
  },
);
